<template>
  <AppModal :modelValue="isOpen" @update:modelValue="updateModelValue" :align-center="true" class="custom-export-modal">
    <h3>Export report</h3>

    <div v-if="!exportSuccess">
      <!-- Multiple Email Recipients -->
      <div class="email-recipients-section">
        <label class="email-label">Email Recipients:</label>

        <div v-for="emailItem in emails" :key="emailItem.id" class="email-input-row">
          <AppInput
            v-model="emailItem.email"
            placeholder="email@example.com"
            :class="{ 'email-input': true, 'email-error': emailItem.email && !isValidEmail(emailItem.email) }"
          />
          <AppButton v-if="emails.length > 1" @click="removeEmailRecipient(emailItem.id)" variant="plain" class="ml-10px">
            <AppIcon color="red" type="x" />
          </AppButton>
        </div>

        <AppButton @click="addEmailRecipient" variant="outlined" class="full-width mt-10px">
          <AppIcon type="plus" color="primary" size="18" class="mr-5px" /> Add another email
        </AppButton>
      </div>

      <ElCheckbox v-if="!isAdmin()" v-model="contactMe" class="mt-10px">
        I would like to be contacted for further discussion
      </ElCheckbox>

      <ReportExportButtons @export="handleExport" />
    </div>

    <ReportExportSuccess v-else />
  </AppModal>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { ElCheckbox } from "element-plus";
import { AppButton, AppIcon, AppInput, AppModal } from "@/components/ui";
import { useReports } from "@/composables/useReports";
import { getCurrentUser, isAdmin } from "@/utilities/token";
import ReportExportButtons from "@/views/ReportDetailsView/components/ReportExportButtons.vue";
import ReportExportSuccess from "@/views/ReportDetailsView/components/ReportExportSuccess.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);
const route = useRoute();
const user = getCurrentUser();

// Use the reports composable for export functionality
const {
  emails,
  contactMe,
  exportSuccess,
  handleExport: handleExportAction,
  addEmailRecipient,
  removeEmailRecipient,
  initializeEmails,
} = useReports();

// Email validation helper
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Initialize emails when modal opens and user data is available
const initializeEmailsIfNeeded = () => {
  if (props.modelValue && user?.email && emails.value.length === 0) {
    initializeEmails(user.email);
  }
};

// Watch for modal opening and user data changes
watch(() => props.modelValue, initializeEmailsIfNeeded, { immediate: true });

// Also try on mount
onMounted(() => {
  initializeEmailsIfNeeded();
});

// Use a computed property to properly handle the two-way binding
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// Helper method to handle update events from the modal
const updateModelValue = (value) => {
  emit("update:modelValue", value);
  if (!value) {
    // Reset state when modal closes
    exportSuccess.value = false;
    emails.value = [];
    contactMe.value = false;
    // Re-initialize with user email when modal reopens
    if (user?.email) {
      setTimeout(() => initializeEmails(user.email), 100);
    }
  }
};

const handleExport = async (type) => {
  const reportId = route.params.id;
  const success = await handleExportAction(type, reportId);

  if (success && type === "pdf") {
    setTimeout(() => {
      emit("update:modelValue", false);
    }, 2000);
  }
};
</script>

<style lang="scss">
.custom-export-modal {
  padding: 30px !important;
  max-width: 480px !important;
  position: relative;
}

.email-recipients-section {
  margin-bottom: 15px;
}

.email-label {
  font-weight: 600;
  font-size: 14px;
  color: $color-ink;
  margin-bottom: 8px;
  display: block;
}

.email-input-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.email-input {
  flex: 1;
}

.email-error .el-input__wrapper {
  box-shadow: 0 0 0 1px $color-error inset;
}
</style>
